import { FilterSubType, QueryOperator, isFilterSubType } from "types";
import { useEffect, useRef, useState } from "react";
import ChevronIcon from "assets/icons/ChevronIcon";
import {
  getFilterSubTypeTitle,
  getOperatorTitle,
} from "helpers/utility/queryFilterHelpers";
import classNames from "classnames";

import styles from "./styles.module.scss";

type OperatorOption<T> = T | { value: T; label: string };

export interface QueryModalOperatorsProps<T> {
  operators: OperatorOption<T>[];
  selectedOperator: T;
  onSelect: (operator: T) => void;
}

export default function QueryModalOperators<T>({
  operators,
  selectedOperator,
  onSelect,
}: QueryModalOperatorsProps<T>) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const getTitleText = (option: OperatorOption<T>) => {
    if (option === null) return "Choose Type";
    if (typeof option === "object" && "label" in option) {
      return option.label;
    }
    if (isFilterSubType(option as FilterSubType))
      return getFilterSubTypeTitle(option as FilterSubType);
    else return getOperatorTitle(option as QueryOperator);
  };

  const handleOperatorClick = (operator: OperatorOption<T>) => {
    const value =
      operator !== null && typeof operator === "object" && "value" in operator
        ? operator.value
        : operator;
    if (value !== selectedOperator) {
      onSelect(value);
    }
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      !dropdownRef.current?.contains(event.target as Node) &&
      !headerRef.current?.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showDropdown]);

  return (
    <div className={styles.queryModalOptions}>
      <div
        ref={headerRef}
        className={styles.dropdownHeader}
        data-testid="query-modal-operators-header"
        onClick={toggleDropdown}
      >
        <span>{getTitleText(selectedOperator)}</span>
        <ChevronIcon direction={showDropdown ? "up" : "down"} />
      </div>
      {showDropdown && (
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          data-testid="query-modal-operators-dropdown"
        >
          {operators.map((operator, index) => (
            <SingleOperator
              key={index}
              operator={operator}
              selected={
                operator !== null &&
                typeof operator === "object" &&
                "value" in operator
                  ? operator.value === selectedOperator
                  : operator === selectedOperator
              }
              getTitleText={getTitleText}
              onClick={handleOperatorClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface SingleOperatorProps<T> {
  selected: boolean;
  operator: OperatorOption<T>;
  onClick: (operator: OperatorOption<T>) => void;
  getTitleText: (operator: OperatorOption<T>) => string;
}

function SingleOperator<T>({
  operator,
  selected,
  onClick,
  getTitleText,
}: SingleOperatorProps<T>) {
  const optionClassnames = classNames(styles.option, {
    [styles.selectedOption]: selected,
  });

  return (
    <div className={optionClassnames} onClick={() => onClick(operator)}>
      {getTitleText(operator)}
    </div>
  );
}
